import { createStore } from './createStore';

/**
 * Video-centric timeline store.
 * 
 * This store manages videos directly at the project level for the flat video-based timeline.
 * Each video is a standalone entity with its own keyframes, audio clips, and metadata.
 * 
 * Note: This replaces the scene-based architecture for the timeline UI.
 * The useScenesStore is kept for backward compatibility but should be considered deprecated.
 */

const initialState = {
  videos: [],
  currentVideoIndex: 0,
  selectedVideoId: null,
  previewingVersionNumber: null, // Version number being previewed in sidebar (applies to selectedVideoId)
  newlyCompletedVideoIds: new Set(), // Videos that just finished generation
  newlyGeneratingVideoIds: new Set(), // Videos that just started generating
};

export const useVideosStore = createStore('VideosStore', (set, get) => ({
  ...initialState,

  /**
   * Replace all videos at once.
   * @param {Array<object>} videos
   */
  setVideos: (videos) =>
    set({
      videos: Array.isArray(videos)
        ? [...videos].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
        : [],
    }),

  /**
   * Add a new video or update if it already exists.
   * @param {object} video
   */
  upsertVideo: (video) =>
    set((state) => {
      if (!video?.id) return state;
      const existingIndex = state.videos.findIndex((v) => v.id === video.id);
      if (existingIndex === -1) {
        // New video — append and sort
        const videos = [...state.videos, video];
        return { videos: videos.sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)) };
      }
      const existing = state.videos[existingIndex];
      // Shallow-check: if nothing changed, return same state reference (prevents re-renders)
      const merged = { ...existing, ...video };
      const hasChanged = Object.keys(merged).some((k) => merged[k] !== existing[k]);
      if (!hasChanged) return state;
      // Only re-sort if orderIndex actually changed
      const needsSort = merged.orderIndex !== existing.orderIndex;
      const newVideos = state.videos.map((v) => (v.id === video.id ? merged : v));
      return { videos: needsSort ? newVideos.sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)) : newVideos };
    }),

  /**
   * Apply partial updates to a video.
   * @param {string} videoId
   * @param {object|Function} updates
   */
  updateVideo: (videoId, updates) =>
    set((state) => {
      const updatedVideos = state.videos.map((video) => {
        if (video.id !== videoId) return video;
        const resolvedUpdates =
          typeof updates === 'function' ? updates(video) : updates;
        return resolvedUpdates ? { ...video, ...resolvedUpdates } : video;
      });
      // Re-sort if orderIndex changed so the timeline reflects the new order.
      const prev = state.videos.find((v) => v.id === videoId);
      const next = updatedVideos.find((v) => v.id === videoId);
      const orderChanged =
        prev && next && (prev.orderIndex ?? 0) !== (next.orderIndex ?? 0);
      return {
        videos: orderChanged
          ? updatedVideos.sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
          : updatedVideos,
      };
    }),

  /**
   * Remove a video from the collection.
   * @param {string} videoId
   */
  deleteVideo: (videoId) =>
    set((state) => ({
      videos: state.videos.filter((video) => video.id !== videoId),
      currentVideoIndex:
        state.currentVideoIndex >= state.videos.length - 1
          ? Math.max(0, state.currentVideoIndex - 1)
          : state.currentVideoIndex,
    })),

  /**
   * Reorder videos based on an ordered array of video IDs.
   * @param {string[]} videoIds - Ordered array of video IDs
   */
  reorderVideos: (videoIds) =>
    set((state) => {
      const videoMap = new Map(state.videos.map((v) => [v.id, v]));
      const reorderedVideos = videoIds
        .map((id, index) => {
          const video = videoMap.get(id);
          return video ? { ...video, orderIndex: index } : null;
        })
        .filter(Boolean);
      return { videos: reorderedVideos };
    }),

  /**
   * Update the currently focused video index.
   * @param {number} index
   */
  setCurrentVideoIndex: (index) =>
    set((state) => {
      const clamped = Math.min(Math.max(index, 0), Math.max(state.videos.length - 1, 0));
      return { currentVideoIndex: clamped };
    }),

  /**
   * Set the selected video ID.
   * @param {string|null} videoId
   */
  setSelectedVideoId: (videoId) =>
    set({ selectedVideoId: videoId, previewingVersionNumber: null }),

  /**
   * Set the version number being previewed in the sidebar.
   * @param {number|null} vn
   */
  setPreviewingVersionNumber: (vn) =>
    set({ previewingVersionNumber: vn ?? null }),

  /**
   * Clear all videos and reset state.
   */
  resetVideos: () => set(() => initialState),

  /**
   * Mark a video as newly completed (just finished generation).
   * @param {string} videoId
   */
  markVideoAsNewlyCompleted: (videoId) =>
    set((state) => ({
      newlyCompletedVideoIds: new Set([...state.newlyCompletedVideoIds, videoId]),
    })),

  /**
   * Clear the "newly completed" status for a video.
   * @param {string} videoId
   */
  clearVideoNewlyCompleted: (videoId) =>
    set((state) => {
      const updated = new Set(state.newlyCompletedVideoIds);
      updated.delete(videoId);
      return { newlyCompletedVideoIds: updated };
    }),

  /**
   * Mark a video as newly generating (freshly started generation).
   * @param {string} videoId
   */
  markVideoAsNewlyGenerating: (videoId) =>
    set((state) => ({
      newlyGeneratingVideoIds: new Set([...state.newlyGeneratingVideoIds, videoId]),
    })),

  /**
   * Clear the "newly generating" status for a video (e.g., when user clicks/views it).
   * @param {string} videoId
   */
  clearVideoNewlyGenerating: (videoId) =>
    set((state) => {
      const updated = new Set(state.newlyGeneratingVideoIds);
      updated.delete(videoId);
      return { newlyGeneratingVideoIds: updated };
    }),

  /**
   * Update video crop values.
   * @param {string} videoId
   * @param {number} cropStartMs
   * @param {number} cropEndMs
   */
  updateVideoCrop: (videoId, cropStartMs, cropEndMs) =>
    set((state) => ({
      videos: state.videos.map((video) =>
        video.id === videoId
          ? { ...video, cropStartMs, cropEndMs }
          : video
      ),
    })),

  /**
   * Update video volume.
   * @param {string} videoId
   * @param {number} volume (0-200)
   */
  updateVideoVolume: (videoId, volume) =>
    set((state) => ({
      videos: state.videos.map((video) =>
        video.id === videoId
          ? { ...video, volume: Math.max(0, Math.min(200, volume)) }
          : video
      ),
    })),

  /**
   * Update video voiceover.
   * @param {string} videoId
   * @param {string} voiceover
   */
  updateVideoVoiceover: (videoId, voiceover) =>
    set((state) => ({
      videos: state.videos.map((video) =>
        video.id === videoId
          ? { ...video, voiceover }
          : video
      ),
    })),

  /**
   * Update video status.
   * @param {string} videoId
   * @param {string} status - pending, generating, completed, failed, dismissed
   */
  updateVideoStatus: (videoId, status) =>
    set((state) => ({
      videos: state.videos.map((video) =>
        video.id === videoId
          ? { ...video, status }
          : video
      ),
    })),

  /**
   * Derived helpers
   */
  getCurrentVideo: () => get().videos[get().currentVideoIndex] ?? null,
  getVideoById: (videoId) => get().videos.find((video) => video.id === videoId) ?? null,
  getVideoByIndex: (index) => get().videos[index] ?? null,
  
  /**
   * Get total duration of all videos (accounting for crops).
   * @returns {number} Total duration in milliseconds
   */
  getTotalDuration: () => {
    return get().videos.reduce((total, video) => {
      const duration = video.duration || 0;
      const cropStart = video.cropStartMs || 0;
      const cropEnd = video.cropEndMs || 0;
      return total + Math.max(0, duration - cropStart - cropEnd);
    }, 0);
  },

  /**
   * Get count of videos that have completed generation.
   * @returns {number}
   */
  getCompletedVideoCount: () => {
    return get().videos.filter((v) => v.status === 'completed' && v.videoUrl).length;
  },

  // ============================================================================
  // Video-Centric Architecture: Centralized ordering and status helpers
  // ============================================================================

  /**
   * Get the next orderIndex for a new video.
   * This ensures new videos are always appended at the end.
   * @returns {number} The next available orderIndex
   */
  getNextOrderIndex: () => {
    const { videos } = get();
    if (videos.length === 0) return 0;
    let maxOrder = -1;
    for (const v of videos) {
      const oi = v?.orderIndex;
      if (Number.isFinite(oi)) maxOrder = Math.max(maxOrder, oi);
    }
    return maxOrder + 1;
  },

  /**
   * Add a video at the end of the timeline with correct orderIndex.
   * Handles optimistic videos by auto-assigning orderIndex if not provided.
   * @param {object} video - Video to add
   * @returns {object} The video with assigned orderIndex
   */
  addVideoAtEnd: (video) => {
    if (!video?.id) return null;
    const nextOrderIndex = get().getNextOrderIndex();
    const videoWithOrder = {
      ...video,
      orderIndex: video.orderIndex ?? nextOrderIndex,
    };
    set((state) => ({
      videos: [...state.videos, videoWithOrder].sort(
        (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)
      ),
    }));
    return videoWithOrder;
  },

  /**
   * Replace an optimistic (temp) video with the real video from the backend.
   * Preserves the orderIndex from the optimistic video.
   * @param {string} tempId - The temporary ID of the optimistic video
   * @param {object} realVideo - The real video data from the backend
   */
  replaceOptimisticVideo: (tempId, realVideo) =>
    set((state) => {
      const optimistic = state.videos.find((v) => v.id === tempId);
      const orderIndex = optimistic?.orderIndex ?? realVideo.orderIndex ?? 0;
      
      // Remove the temp video AND any existing video with the real ID.
      // The realtime subscription may have already inserted the real video
      // before this replacement runs, so we must deduplicate.
      const filtered = state.videos.filter(
        (v) => v.id !== tempId && v.id !== realVideo.id
      );
      const updated = [...filtered, { ...realVideo, orderIndex, isPending: false }];
      
      return {
        videos: updated.sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)),
      };
    }),

  /**
   * Remove an optimistic video (on creation failure).
   * @param {string} tempId - The temporary ID to remove
   */
  removeOptimisticVideo: (tempId) =>
    set((state) => ({
      videos: state.videos.filter((v) => v.id !== tempId),
    })),

  /**
   * Get IDs of all videos currently generating.
   * @returns {string[]} Array of video IDs with status 'generating' or isPending=true
   */
  getGeneratingVideoIds: () => {
    return get().videos
      .filter((v) => v.status === 'generating' || v.isPending)
      .map((v) => v.id);
  },

  /**
   * Check if a specific video is currently generating.
   * @param {string} videoId
   * @returns {boolean}
   */
  isVideoGenerating: (videoId) => {
    const video = get().videos.find((v) => v.id === videoId);
    return video?.status === 'generating' || video?.isPending === true;
  },

  /**
   * Get the selected video object (resolved from selectedVideoId).
   * @returns {object|null}
   */
  getSelectedVideo: () => {
    const { videos, selectedVideoId } = get();
    if (!selectedVideoId) return null;
    return videos.find((v) => v.id === selectedVideoId) ?? null;
  },
}));
